import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  BookOpenIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  QrCodeIcon,
  TagIcon,
  DocumentDuplicateIcon,
  FunnelIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserIcon,
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import {
  getBooks,
  getBook,
  getBookByBarcode,
  createBook,
  updateBook,
  deleteBook,
  getCategories,
  addBookCopy,
  type Book,
  type BookCategory,
  type CreateBookRequest,
  type BookCopy,
} from '@/api/libraryApi'
import toast from 'react-hot-toast'

const bookSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  subtitle: z.string().optional().nullable(),
  authors: z.string().min(1, 'At least one author is required'),
  isbn: z.string().optional().nullable(),
  isbn13: z.string().optional().nullable(),
  barcode: z.string().min(1, 'Barcode is required'),
  publisher: z.string().optional().nullable(),
  publishedDate: z.string().optional().nullable(),
  edition: z.string().optional().nullable(),
  language: z.string().default('English'),
  pageCount: z.number().optional().nullable(),
  description: z.string().optional().nullable(),
  coverImage: z.string().optional().nullable(),
  category: z.string().min(1, 'Category is required'),
  subcategory: z.string().optional().nullable(),
  tags: z.string().optional().nullable(),
  totalCopies: z.number().min(1, 'At least 1 copy required').default(1),
  shelfLocation: z.string().optional().nullable(),
  deweyDecimal: z.string().optional().nullable(),
  acquisitionSource: z.enum(['purchase', 'donation', 'transfer', 'other']).optional().nullable(),
  price: z.number().min(0).optional().nullable(),
  currency: z.string().default('KES'),
  isReferenceOnly: z.boolean().default(false),
})

type BookFormData = z.infer<typeof bookSchema>

const BooksPage: React.FC = () => {
  const { user } = useAuth()
  const [books, setBooks] = useState<Book[]>([])
  const [categories, setCategories] = useState<BookCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [availabilityFilter, setAvailabilityFilter] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showScanModal, setShowScanModal] = useState(false)
  const [selectedBook, setSelectedBook] = useState<Book | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalBooks, setTotalBooks] = useState(0)
  const pageSize = 12
  const barcodeInputRef = useRef<HTMLInputElement>(null)

  const bookForm = useForm<BookFormData>({
    resolver: zodResolver(bookSchema),
    defaultValues: {
      title: '',
      authors: '',
      isbn: '',
      barcode: '',
      language: 'English',
      totalCopies: 1,
      currency: 'KES',
      isReferenceOnly: false,
    },
  })

  useEffect(() => {
    fetchBooks()
    fetchCategories()
  }, [currentPage, searchQuery, categoryFilter, availabilityFilter])

  const fetchBooks = async () => {
    try {
      setLoading(true)
      const response = await getBooks({
        page: currentPage,
        limit: pageSize,
        search: searchQuery || undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        hasAvailableCopies: availabilityFilter === 'available' ? true : undefined,
      })
      setBooks(response.data)
      setTotalPages(response.pagination.pages)
      setTotalBooks(response.pagination.total)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load books')
    } finally {
      setLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await getCategories()
      setCategories(response.data)
    } catch (error: any) {
      console.error('Failed to load categories:', error)
    }
  }

  const handleAddBook = async (data: BookFormData) => {
    try {
      // Clean up and prepare data for creating a new book
      const bookData: Record<string, any> = {
        title: data.title,
        authors: data.authors.split(',').map((a) => a.trim()),
        barcode: data.barcode,
        category: data.category,
        language: data.language || 'English',
        totalCopies: data.totalCopies || 1,
        currency: data.currency || 'KES',
        isReferenceOnly: data.isReferenceOnly || false,
      }

      // Only add optional fields if they have values
      if (data.subtitle?.trim()) bookData.subtitle = data.subtitle.trim()
      if (data.isbn?.trim()) bookData.isbn = data.isbn.trim()
      if (data.isbn13?.trim()) bookData.isbn13 = data.isbn13.trim()
      if (data.publisher?.trim()) bookData.publisher = data.publisher.trim()
      if (data.publishedDate?.trim()) bookData.publishedDate = data.publishedDate.trim()
      if (data.edition?.trim()) bookData.edition = data.edition.trim()
      if (data.pageCount) bookData.pageCount = data.pageCount
      if (data.description?.trim()) bookData.description = data.description.trim()
      if (data.coverImage?.trim()) bookData.coverImage = data.coverImage.trim()
      if (data.subcategory?.trim()) bookData.subcategory = data.subcategory.trim()
      if (data.tags?.trim()) bookData.tags = data.tags.split(',').map((t) => t.trim())
      if (data.shelfLocation?.trim()) bookData.shelfLocation = data.shelfLocation.trim()
      if (data.deweyDecimal?.trim()) bookData.deweyDecimal = data.deweyDecimal.trim()
      if (data.acquisitionSource) bookData.acquisitionSource = data.acquisitionSource
      if (data.price != null && data.price >= 0) bookData.price = data.price

      await createBook(bookData as CreateBookRequest)
      toast.success('Book added successfully')
      setShowAddModal(false)
      bookForm.reset()
      fetchBooks()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to add book')
    }
  }

  const handleUpdateBook = async (data: BookFormData) => {
    if (!selectedBook) return
    try {
      // Clean up empty values and prepare data
      const bookData: Record<string, any> = {
        title: data.title,
        authors: data.authors.split(',').map((a) => a.trim()),
        barcode: data.barcode,
        category: data.category,
        language: data.language || 'English',
        totalCopies: data.totalCopies || 1,
        currency: data.currency || 'KES',
        isReferenceOnly: data.isReferenceOnly || false,
      }

      // Only add optional fields if they have values
      if (data.subtitle?.trim()) bookData.subtitle = data.subtitle.trim()
      if (data.isbn?.trim()) bookData.isbn = data.isbn.trim()
      if (data.isbn13?.trim()) bookData.isbn13 = data.isbn13.trim()
      if (data.publisher?.trim()) bookData.publisher = data.publisher.trim()
      if (data.publishedDate?.trim()) bookData.publishedDate = data.publishedDate.trim()
      if (data.edition?.trim()) bookData.edition = data.edition.trim()
      if (data.pageCount) bookData.pageCount = data.pageCount
      if (data.description?.trim()) bookData.description = data.description.trim()
      if (data.coverImage?.trim()) bookData.coverImage = data.coverImage.trim()
      if (data.subcategory?.trim()) bookData.subcategory = data.subcategory.trim()
      if (data.tags?.trim()) bookData.tags = data.tags.split(',').map((t) => t.trim())
      if (data.shelfLocation?.trim()) bookData.shelfLocation = data.shelfLocation.trim()
      if (data.deweyDecimal?.trim()) bookData.deweyDecimal = data.deweyDecimal.trim()
      if (data.acquisitionSource) bookData.acquisitionSource = data.acquisitionSource
      if (data.price != null && data.price >= 0) bookData.price = data.price

      await updateBook(selectedBook._id, bookData)
      toast.success('Book updated successfully')
      setShowEditModal(false)
      setSelectedBook(null)
      fetchBooks()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update book')
    }
  }

  const handleDeleteBook = async (book: Book) => {
    if (!confirm(`Are you sure you want to delete "${book.title}"?`)) return
    try {
      await deleteBook(book._id)
      toast.success('Book deleted successfully')
      fetchBooks()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete book')
    }
  }

  const handleViewBook = async (book: Book) => {
    try {
      const response = await getBook(book._id)
      setSelectedBook(response.data)
      setShowDetailModal(true)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load book details')
    }
  }

  const handleEditBook = (book: Book) => {
    setSelectedBook(book)
    bookForm.reset({
      title: book.title,
      subtitle: book.subtitle || '',
      authors: book.authors.join(', '),
      isbn: book.isbn,
      isbn13: book.isbn13 || '',
      barcode: book.barcode,
      publisher: book.publisher || '',
      publishedDate: book.publishedDate?.split('T')[0] || '',
      edition: book.edition || '',
      language: book.language,
      pageCount: book.pageCount,
      description: book.description || '',
      coverImage: book.coverImage || '',
      category: typeof book.category === 'string' ? book.category : book.category._id,
      subcategory: book.subcategory ? (typeof book.subcategory === 'string' ? book.subcategory : book.subcategory._id) : '',
      tags: book.tags?.join(', ') || '',
      totalCopies: book.totalCopies,
      shelfLocation: book.shelfLocation || '',
      deweyDecimal: book.deweyDecimal || '',
      acquisitionSource: book.acquisitionSource,
      price: book.price,
      currency: book.currency,
      isReferenceOnly: book.isReferenceOnly,
    })
    setShowEditModal(true)
  }

  const handleBarcodeScan = async (barcode: string) => {
    if (!barcode.trim()) return
    try {
      const response = await getBookByBarcode(barcode.trim())
      if (response.data.scannedType === 'book') {
        setSelectedBook(response.data)
        setShowScanModal(false)
        setShowDetailModal(true)
      } else if (response.data.scannedType === 'copy') {
        setSelectedBook(response.data.book)
        setShowScanModal(false)
        setShowDetailModal(true)
      }
      toast.success('Book found!')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Book not found')
    }
  }

  const handleAddCopy = async (bookId: string) => {
    try {
      await addBookCopy(bookId, { condition: 'excellent' })
      toast.success('Copy added successfully')
      // Refresh the book details
      const response = await getBook(bookId)
      setSelectedBook(response.data)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to add copy')
    }
  }

  const getCategoryName = (category: string | BookCategory) => {
    if (typeof category === 'string') {
      const found = categories.find((c) => c._id === category)
      return found?.name || 'Unknown'
    }
    return category.name
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Books Management</h1>
          <p className="text-gray-500 mt-1">{totalBooks} books in the library</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowScanModal(true)}
            className="flex items-center gap-2"
          >
            <QrCodeIcon className="w-5 h-5" />
            Scan Barcode
          </Button>
          <Button onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
            <PlusIcon className="w-5 h-5" />
            Add Book
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search books by title, author, ISBN, or barcode..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setCurrentPage(1)
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value)
                setCurrentPage(1)
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.name}
                </option>
              ))}
            </select>
            <select
              value={availabilityFilter}
              onChange={(e) => {
                setAvailabilityFilter(e.target.value)
                setCurrentPage(1)
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Books</option>
              <option value="available">Available Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Books Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Book</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author(s)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ISBN</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Availability</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {books.map((book) => (
                    <tr key={book._id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-8 bg-gray-100 rounded flex items-center justify-center">
                            {book.coverImage ? (
                              <img
                                src={book.coverImage}
                                alt={book.title}
                                className="h-10 w-8 object-cover rounded"
                              />
                            ) : (
                              <BookOpenIcon className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900 max-w-xs truncate" title={book.title}>
                              {book.title}
                            </div>
                            {book.isReferenceOnly && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                Reference
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate" title={book.authors.join(', ')}>
                          {book.authors.join(', ')}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-mono">{book.isbn}</div>
                        <div className="text-xs text-gray-500">{book.barcode}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          {getCategoryName(book.category)}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{book.shelfLocation || '-'}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            book.availableCopies > 0
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {book.availableCopies}/{book.totalCopies}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => handleViewBook(book)}
                            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                            title="View"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditBook(book)}
                            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                            title="Edit"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteBook(book)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {books.length === 0 && (
              <div className="text-center py-12">
                <BookOpenIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No books found</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-500">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalBooks)} of {totalBooks} books
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Barcode Scan Modal */}
      <AnimatePresence>
        {showScanModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowScanModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Scan Barcode</h2>
                <button onClick={() => setShowScanModal(false)} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              <div className="text-center mb-6">
                <QrCodeIcon className="w-16 h-16 text-primary-500 mx-auto mb-4" />
                <p className="text-gray-600">Scan or enter the book barcode</p>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  const formData = new FormData(e.currentTarget)
                  handleBarcodeScan(formData.get('barcode') as string)
                }}
              >
                <input
                  ref={barcodeInputRef}
                  type="text"
                  name="barcode"
                  placeholder="Enter or scan barcode..."
                  autoFocus
                  className="w-full px-4 py-3 text-center text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <Button type="submit" className="w-full mt-4">
                  Search Book
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add/Edit Book Modal */}
      <AnimatePresence>
        {(showAddModal || showEditModal) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowAddModal(false)
              setShowEditModal(false)
              setSelectedBook(null)
              bookForm.reset()
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {showAddModal ? 'Add New Book' : 'Edit Book'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddModal(false)
                    setShowEditModal(false)
                    setSelectedBook(null)
                    bookForm.reset()
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={bookForm.handleSubmit(showAddModal ? handleAddBook : handleUpdateBook)}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input
                      {...bookForm.register('title')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    {bookForm.formState.errors.title && (
                      <p className="text-red-500 text-xs mt-1">{bookForm.formState.errors.title.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Authors * (comma-separated)</label>
                    <input
                      {...bookForm.register('authors')}
                      placeholder="John Doe, Jane Smith"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    {bookForm.formState.errors.authors && (
                      <p className="text-red-500 text-xs mt-1">{bookForm.formState.errors.authors.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ISBN *</label>
                    <input
                      {...bookForm.register('isbn')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    {bookForm.formState.errors.isbn && (
                      <p className="text-red-500 text-xs mt-1">{bookForm.formState.errors.isbn.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Barcode *</label>
                    <input
                      {...bookForm.register('barcode')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    {bookForm.formState.errors.barcode && (
                      <p className="text-red-500 text-xs mt-1">{bookForm.formState.errors.barcode.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                    <select
                      {...bookForm.register('category')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select category</option>
                      {categories.map((cat) => (
                        <option key={cat._id} value={cat._id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                    {bookForm.formState.errors.category && (
                      <p className="text-red-500 text-xs mt-1">{bookForm.formState.errors.category.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Publisher</label>
                    <input
                      {...bookForm.register('publisher')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Published Date</label>
                    <input
                      type="date"
                      {...bookForm.register('publishedDate')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Number of Copies</label>
                    <input
                      type="number"
                      min="1"
                      {...bookForm.register('totalCopies', { valueAsNumber: true })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Shelf Location</label>
                    <input
                      {...bookForm.register('shelfLocation')}
                      placeholder="e.g., A-12-3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      {...bookForm.register('price', { valueAsNumber: true })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
                    <input
                      {...bookForm.register('tags')}
                      placeholder="fiction, adventure, classic"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      {...bookForm.register('description')}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        {...bookForm.register('isReferenceOnly')}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">Reference Only (cannot be borrowed)</span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAddModal(false)
                      setShowEditModal(false)
                      setSelectedBook(null)
                      bookForm.reset()
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={bookForm.formState.isSubmitting}>
                    {bookForm.formState.isSubmitting ? 'Saving...' : showAddModal ? 'Add Book' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Book Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedBook && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowDetailModal(false)
              setSelectedBook(null)
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Book Details</h2>
                <button
                  onClick={() => {
                    setShowDetailModal(false)
                    setSelectedBook(null)
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Book Cover */}
                <div className="md:col-span-1">
                  <div className="aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden">
                    {selectedBook.coverImage ? (
                      <img
                        src={selectedBook.coverImage}
                        alt={selectedBook.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpenIcon className="w-24 h-24 text-gray-300" />
                      </div>
                    )}
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Status</span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          selectedBook.availableCopies > 0
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {selectedBook.availableCopies} / {selectedBook.totalCopies} available
                      </span>
                    </div>
                    {selectedBook.isReferenceOnly && (
                      <div className="flex items-center gap-2 text-amber-600 text-sm">
                        <ExclamationTriangleIcon className="w-4 h-4" />
                        <span>Reference Only</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Book Info */}
                <div className="md:col-span-2 space-y-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{selectedBook.title}</h3>
                    {selectedBook.subtitle && (
                      <p className="text-gray-600">{selectedBook.subtitle}</p>
                    )}
                    <p className="text-primary-600 mt-1">{selectedBook.authors.join(', ')}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">ISBN:</span>
                      <span className="ml-2 text-gray-900">{selectedBook.isbn}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Barcode:</span>
                      <span className="ml-2 text-gray-900">{selectedBook.barcode}</span>
                    </div>
                    {selectedBook.publisher && (
                      <div>
                        <span className="text-gray-500">Publisher:</span>
                        <span className="ml-2 text-gray-900">{selectedBook.publisher}</span>
                      </div>
                    )}
                    {selectedBook.publishedDate && (
                      <div>
                        <span className="text-gray-500">Published:</span>
                        <span className="ml-2 text-gray-900">
                          {new Date(selectedBook.publishedDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Category:</span>
                      <span className="ml-2 text-gray-900">{getCategoryName(selectedBook.category)}</span>
                    </div>
                    {selectedBook.shelfLocation && (
                      <div>
                        <span className="text-gray-500">Location:</span>
                        <span className="ml-2 text-gray-900">{selectedBook.shelfLocation}</span>
                      </div>
                    )}
                    {selectedBook.price && (
                      <div>
                        <span className="text-gray-500">Price:</span>
                        <span className="ml-2 text-gray-900">
                          {selectedBook.currency} {selectedBook.price.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {selectedBook.description && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Description</h4>
                      <p className="text-gray-600 text-sm">{selectedBook.description}</p>
                    </div>
                  )}

                  {selectedBook.tags && selectedBook.tags.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Tags</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedBook.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Book Copies */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">Copies</h4>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAddCopy(selectedBook._id)}
                        className="flex items-center gap-1"
                      >
                        <DocumentDuplicateIcon className="w-4 h-4" />
                        Add Copy
                      </Button>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {selectedBook.copies?.map((copy: BookCopy) => {
                        const borrowing = (copy as any).currentBorrowing;
                        const borrower = borrowing?.borrower;
                        return (
                          <div
                            key={copy._id}
                            className="p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-medium text-gray-900">
                                  Copy #{copy.copyNumber}
                                </span>
                                <span className="text-gray-400 ml-2 text-sm">{copy.barcode}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span
                                  className={`px-2 py-1 text-xs rounded-full ${
                                    copy.condition === 'excellent'
                                      ? 'bg-green-100 text-green-700'
                                      : copy.condition === 'good'
                                      ? 'bg-blue-100 text-blue-700'
                                      : copy.condition === 'fair'
                                      ? 'bg-yellow-100 text-yellow-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}
                                >
                                  {copy.condition}
                                </span>
                                <span
                                  className={`px-2 py-1 text-xs rounded-full ${
                                    copy.status === 'available'
                                      ? 'bg-green-100 text-green-700'
                                      : copy.status === 'borrowed'
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-gray-100 text-gray-700'
                                  }`}
                                >
                                  {copy.status}
                                </span>
                              </div>
                            </div>
                            {/* Show borrower info if the copy is borrowed */}
                            {copy.status === 'borrowed' && borrower && (
                              <div className="mt-2 pt-2 border-t border-gray-200">
                                <div className="flex items-center gap-2 text-sm">
                                  <div className="flex-shrink-0 w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                                    {borrower.profileImage ? (
                                      <img
                                        src={borrower.profileImage}
                                        alt=""
                                        className="w-full h-full object-cover rounded-full"
                                      />
                                    ) : (
                                      <UserIcon className="w-3 h-3 text-gray-500" />
                                    )}
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {borrower.firstName} {borrower.lastName}
                                      {borrower.studentId && (
                                        <span className="text-gray-500 ml-1">({borrower.studentId})</span>
                                      )}
                                    </p>
                                    <div className="text-xs text-gray-500">
                                      {borrower.phone && <span>{borrower.phone}</span>}
                                      {borrower.phone && borrower.email && <span> • </span>}
                                      {borrower.email && <span>{borrower.email}</span>}
                                    </div>
                                    {borrowing.dueDate && (
                                      <p className="text-xs text-amber-600">
                                        Due: {new Date(borrowing.dueDate).toLocaleDateString()}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {(!selectedBook.copies || selectedBook.copies.length === 0) && (
                        <p className="text-sm text-gray-500 text-center py-4">No copies found</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDetailModal(false)
                    handleEditBook(selectedBook)
                  }}
                >
                  <PencilIcon className="w-4 h-4 mr-2" />
                  Edit Book
                </Button>
                <Button
                  onClick={() => {
                    setShowDetailModal(false)
                    setSelectedBook(null)
                  }}
                >
                  Close
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default BooksPage
